import React, { useState, useEffect } from 'react';
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { useFirebase } from '@/contexts/FirebaseContext';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Plus, Edit, Trash2, MapPin } from 'lucide-react';

interface Area {
  id: string;
  name: string;
  postcode: string;
  createdAt: any;
  updatedAt: any;
}

const AreasManagement: React.FC = () => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    postcode: ''
  });
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { getFirestore } = useFirebase();

  // Fetch areas on component mount
  useEffect(() => {
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      console.log('Fetching areas from Firestore...');
      const areasRef = collection(db, 'areas');
      const snapshot = await getDocs(areasRef);
      const areasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Area[];
      console.log('Areas fetched:', areasData);
      setAreas(areasData);
    } catch (error) {
      console.error('Error fetching areas:', error);
      toast({
        title: "Error",
        description: "Failed to fetch areas. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errs: any = {};
    if (!formData.name.trim()) errs.name = 'Area name is required';
    if (!formData.postcode.trim()) errs.postcode = 'Postcode is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddArea = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const db = getFirestore();
      const areasRef = collection(db, 'areas');
      
      console.log('Adding area to Firestore:', formData);
      const docRef = await addDoc(areasRef, {
        name: formData.name.trim(),
        postcode: formData.postcode.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log('Area added successfully with ID:', docRef.id);

      toast({
        title: "Success",
        description: "Area added successfully!",
      });

      setFormData({ name: '', postcode: '' });
      setIsAddDialogOpen(false);
      fetchAreas();
    } catch (error) {
      console.error('Error adding area:', error);
      toast({
        title: "Error",
        description: "Failed to add area. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditArea = async () => {
    if (!validateForm() || !editingArea) return;

    try {
      setIsSubmitting(true);
      const db = getFirestore();
      const areaRef = doc(db, 'areas', editingArea.id);
      
      console.log('Updating area in Firestore:', editingArea.id, formData);
      await updateDoc(areaRef, {
        name: formData.name.trim(),
        postcode: formData.postcode.trim(),
        updatedAt: serverTimestamp(),
      });
      console.log('Area updated successfully');

      toast({
        title: "Success",
        description: "Area updated successfully!",
      });

      setFormData({ name: '', postcode: '' });
      setEditingArea(null);
      setIsEditDialogOpen(false);
      fetchAreas();
    } catch (error) {
      console.error('Error updating area:', error);
      toast({
        title: "Error",
        description: "Failed to update area. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteArea = async (areaId: string, areaName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${areaName}"?`)) return;

    try {
      const db = getFirestore();
      const areaRef = doc(db, 'areas', areaId);
      console.log('Deleting area from Firestore:', areaId);
      await deleteDoc(areaRef);
      console.log('Area deleted successfully');

      toast({
        title: "Success",
        description: "Area deleted successfully!",
      });

      fetchAreas();
    } catch (error) {
      console.error('Error deleting area:', error);
      toast({
        title: "Error",
        description: "Failed to delete area. Please try again.",
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (area: Area) => {
    setEditingArea(area);
    setFormData({
      name: area.name,
      postcode: area.postcode
    });
    setIsEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditingArea(null);
    setFormData({ name: '', postcode: '' });
    setErrors({});
    setIsEditDialogOpen(false);
  };

  const closeAddDialog = () => {
    setFormData({ name: '', postcode: '' });
    setErrors({});
    setIsAddDialogOpen(false);
  };

  return (
    <DashboardLayout role="admin">
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Areas Covered</h1>
            <p className="text-gray-600 mt-2">Manage the areas and postcodes covered by your driving school</p>
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Area
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Areas List
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading areas...</p>
              </div>
            ) : areas.length === 0 ? (
              <div className="text-center py-8">
                <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No areas added yet. Click "Add Area" to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Area Name</th>
                      <th className="text-left py-3 px-4 font-semibold">Postcode</th>
                      <th className="text-left py-3 px-4 font-semibold">Created</th>
                      <th className="text-right py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areas.map((area) => (
                      <tr key={area.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{area.name}</td>
                        <td className="py-3 px-4 font-mono">{area.postcode}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {area.createdAt?.toDate?.() 
                            ? area.createdAt.toDate().toLocaleDateString()
                            : 'N/A'
                          }
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(area)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteArea(area.id, area.name)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Area Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Area</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="areaName">Area Name</Label>
                <Input
                  id="areaName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Central London"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="areaPostcode">Postcode</Label>
                <Input
                  id="areaPostcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="e.g., W1W 5PF"
                  className={errors.postcode ? 'border-red-500' : ''}
                />
                {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={closeAddDialog}>
                Cancel
              </Button>
              <Button onClick={handleAddArea} disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Area'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Area Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Area</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="editAreaName">Area Name</Label>
                <Input
                  id="editAreaName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Central London"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="editAreaPostcode">Postcode</Label>
                <Input
                  id="editAreaPostcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="e.g., W1W 5PF"
                  className={errors.postcode ? 'border-red-500' : ''}
                />
                {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={closeEditDialog}>
                Cancel
              </Button>
              <Button onClick={handleEditArea} disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update Area'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AreasManagement; 